import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { To, useNavigate } from 'react-router';

import { useLazyQuery, useMutation } from '@apollo/client/react';

import {
  AlertType,
  useApiTranslationMapper,
  ProductMediaUploadQuery,
  ProductMediaUploadQueryVariables,
} from '@anyx/common-graphql';
import { toast } from '@anyx/shared-ui';
import {
  FilesUtils,
  reportAppError,
} from '@anyx/shared-utils';

import { PRODUCT_MEDIA_UPLOAD } from './graphql';

export const useProductUploadMedia = ({
  redirectTo,
  options,
}: {
  redirectTo?: To;
  options?: useMutation.Options<ProductMediaUploadQuery, ProductMediaUploadQueryVariables>;
} = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('shared');
  const { getErrorMessage, getTranslationKey } = useApiTranslationMapper();
  const [uploadProductMedia, { loading: uploading }] = useLazyQuery(PRODUCT_MEDIA_UPLOAD);

  const handleUploadProduct = useCallback(
    async ({ file }: { file?: File }) => {
      try {
        if (!file) {
          return;
        }

        const allowedImageMimeTypes = Object.keys(FilesUtils.MAGIC_BYTES_SIGNATURES).filter(
          (mimeType) => mimeType.startsWith('image/')
        );
        const isValidContent = await FilesUtils.validateMagicBytes(file, allowedImageMimeTypes);
        if (!isValidContent) {
          toast.error(
            t('shared.field.media.error.fileInvalidType', {
              ns: 'shared',
              types: allowedImageMimeTypes
                .map((type) => type.split('/')[1]?.toUpperCase())
                .join(', '),
            })
          );
          return;
        }

        const sanitizedFileName = FilesUtils.sanitizeFileName(file.name);

        const response = await uploadProductMedia({
          ...options,
          variables: {
            fileName: sanitizedFileName,
          },
        });

        if (response.error) throw response.error;

        if (!response.data?.productMediaUpload?.signedUrl) {
          reportAppError(new Error("Couldn't generate Product Upload Signed Url"));
          toast.error(
            getTranslationKey({
              namespace: 'gql',
              type: AlertType.ERROR,
              path: 'upload',
              message: 'uploadUrl',
            })
          );
          return;
        }

        const fileUrl = await FilesUtils.uploadFile(
          file,
          response.data?.productMediaUpload?.signedUrl
        );

        toast.success(
          getTranslationKey({
            namespace: 'gql',
            type: AlertType.SUCCESS,
            path: 'product',
            message: 'uploadProduct',
          })
        );

        if (redirectTo) {
          navigate(redirectTo);
        }

        return fileUrl;
      } catch (e: unknown) {
        reportAppError(e);
        toast.error(
          getTranslationKey({
            namespace: 'gql',
            type: AlertType.ERROR,
            path: 'product',
            message: getErrorMessage(e),
          })
        );
        return;
      }
    },
    [uploadProductMedia, options, getTranslationKey, redirectTo, navigate, getErrorMessage, t]
  );

  return { handleUploadProduct, loading: uploading };
};
